import SelfCareCalendarModal from 'components/global/CalendarModal/forks/SelfcareCalendarModal'
import OpeningHoursModal from 'components/global/OpeningHoursModal'
import Box from 'components/shared/Box'
import Datepicker from 'components/shared/Datepicker'
import Info from 'components/shared/Info'
import Text from 'components/shared/Text'
import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import EditorSummary from './EditorSummary'
import styled from 'styled-components'
import { respondAbove } from 'styles/mediaQueries'
import { useAppSelector } from 'store/hooks'
import { useSelfcare } from 'store/selfcare-slice/hooks'
import { useFindTicketProductBundlesQuery } from 'store/api-slice/hooks'
import { useApp } from 'store/steps'
import { formatPrice } from 'helpers/helpers'
import { TicketBundleReservation } from 'store/selfcare-slice/types'

interface BundleEditorProps {
    bundle: TicketBundleReservation
    onCancel: () => void
    index: number
}

const CustomWrapper = styled(Box)`
    @media ${respondAbove.xl} {
        max-width: 230px;
    }
`

const BundleEditor = ({ bundle, onCancel, index }: BundleEditorProps) => {
    const { language } = useApp()
    const { formatMessage } = useIntl()
    const { editItem, isEditProcessing } = useSelfcare()

    const { transport, cabanas } = useAppSelector((state) => state.selfcare)

    const [isCalendarOpened, setIsCalendarOpened] = useState(false)
    const [isHoursModalOpened, setIsHoursModalOpened] = useState(false)
    const [isSelectedDateAvailable, setIsSelectedDateAvailable] = useState(true)

    const { original, changes } = bundle
    const dataToDisplay = changes || original
    const { detail } = dataToDisplay || {}
    const { orderBundleId, date: originalDate, boxId } = detail || {}

    const [date, setDate] = useState<string>(originalDate)
    const [newPrice, setNewPrice] = useState<number | null>(null)

    const isOriginalDateChanged = date !== originalDate
    const isBusTicketInReservation = transport && transport.length > 0
    const isCabanaInReservation = cabanas && cabanas.length > 0

    const hasPotentiallyConnectedItemsWarning =
        isOriginalDateChanged &&
        (isBusTicketInReservation || isCabanaInReservation)
    const isWeekendPrice =
        newPrice && original.price && newPrice > original.price

    const { data, isFetching } = useFindTicketProductBundlesQuery(
        {
            _locale: language,
            startDate: date,
            endDate: date,
            cartType: 'selfcare',
        },
        {
            skip: !isOriginalDateChanged,
        }
    )

    const handleDateChange = (date: string) => {
        setNewPrice(null)
        setDate(date)
    }

    useEffect(() => {
        setIsSelectedDateAvailable(true)
    }, [date])

    useEffect(() => {
        if (isFetching) return

        const bundlesData = data?.productBundles
        const { status, dates } = bundlesData?.[0] || {}

        if (
            !bundlesData ||
            !bundlesData.length ||
            !dates?.length ||
            !status?.active
        ) {
            isOriginalDateChanged && setIsSelectedDateAvailable(false)
            setNewPrice(null)
            return
        }

        const { unitPrice } = bundlesData[0]
        setNewPrice(unitPrice ?? null)
    }, [data, isFetching])

    return (
        <Box isFullWidth gap={16} mt={[0, 8]}>
            <Text as="h4" variant="heading4">
                <FormattedMessage
                    id="selfcare.bundles.date_change"
                    defaultMessage="Zmiana daty"
                />
            </Text>
            <CustomWrapper isFullWidth>
                <Datepicker
                    openCalendar={() => setIsCalendarOpened(true)}
                    id="edit-bundle-date"
                    value={[date]}
                    label={formatMessage({
                        id: 'global.calendar.change_ticket_label',
                        defaultMessage: 'Data',
                    })}
                    hasNoMaxWidth
                    testId={`selfcare-bundle-${index}-trigger-stay-date`}
                />
            </CustomWrapper>
            {(hasPotentiallyConnectedItemsWarning ||
                (isWeekendPrice && !!newPrice) ||
                !isSelectedDateAvailable) && (
                <Box mt={[16, 32]} mb={[0, 16]} gap={16} isFullWidth>
                    {hasPotentiallyConnectedItemsWarning && (
                        <Info color="info_warning" hasBorder hasNoBackground>
                            <FormattedMessage
                                id="selfcare.bundle_and_upsell_conflict_warning"
                                defaultMessage="Masz wykupione dodatkowe usługi. Upewnij się, czy któreś z nich nie wymagają zmiany. Możesz je edytować po zapisaniu zmian w tym pakiecie."
                            />
                        </Info>
                    )}
                    {!!isWeekendPrice && !!newPrice && (
                        <Info color="info">
                            <FormattedMessage
                                id="selfcare.bundles.higher_price_info"
                                defaultMessage="W weekendy i święta cena biletów w pakiecie to {price} zł."
                                values={{
                                    price: formatPrice(newPrice),
                                }}
                            />
                        </Info>
                    )}
                    {!isSelectedDateAvailable && (
                        <Info color="error">
                            <FormattedMessage
                                id="selfcare.bundles.unavailable_date_info"
                                defaultMessage="Pakiet Family jest niedostępny w wybranym terminie. Wybierz inną datę."
                            />
                        </Info>
                    )}
                </Box>
            )}
            <EditorSummary
                handleCancel={onCancel}
                updatedTicket={{
                    productType: 'bundle',
                    detail: {
                        date,
                        boxId,
                        orderBundleId,
                    },
                }}
                currentPrice={dataToDisplay.price || 0}
                newPrice={newPrice || null}
                isSavingDisabled={
                    !isOriginalDateChanged || !isSelectedDateAvailable
                }
                editItem={editItem}
                isFetching={isEditProcessing}
                index={index}
                testIdPrefix="selfcare-bundle"
            />
            <SelfCareCalendarModal
                isOpened={isCalendarOpened}
                close={() => setIsCalendarOpened(false)}
                openHoursModal={() => setIsHoursModalOpened(true)}
                customConfirmDate={handleDateChange}
                currentDate={date}
                testIdPrefix="selfcare-bundle"
                isCurrentDayDisabled
            />
            <OpeningHoursModal
                isOpened={isHoursModalOpened}
                close={() => setIsHoursModalOpened(false)}
            />
        </Box>
    )
}

export default BundleEditor
